import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  Input,
  signal,
  computed,
  ChangeDetectorRef,
  DestroyRef,
  Directive,
  inject,
  output,
  ViewChild,
  ElementRef,
  effect,
  OnDestroy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { TranslatePipe } from '@ngx-translate/core';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';
import { EntityVisualComponent } from '../entity-visual/entity-visual';
import { Avatar } from '../avatar/avatar';
import { ZagIcon } from '../icon/icon';
import { FormField } from '../form-field/form-field';

/**
 * Case-insensitive fuzzy match: every character in `pattern` must appear in
 * `text` in order, but not necessarily consecutive. E.g. "rom" matches
 * "room@contoso.com", "cal" matches "Calendar".
 */
function fuzzyMatch(pattern: string, text: string): boolean {
  const lower = text.toLowerCase();
  let pi = 0;
  for (let ti = 0; ti < lower.length && pi < pattern.length; ti++) {
    if (lower[ti] === pattern[pi]) pi++;
  }
  return pi === pattern.length;
}

// Check-All selects all, uncheck-All clears, manual completion re-marks.
export function toggleSelectAllValue<T>(current: readonly T[], next: readonly T[], allValue: T, allIds: readonly T[]): T[] {
  const hadAll = current.includes(allValue);
  const hasAll = next.includes(allValue);
  const ids = next.filter(id => id !== allValue);
  if (hasAll && !hadAll) return [allValue, ...allIds];
  if (!hasAll && hadAll) return [];
  return ids.length === allIds.length ? [allValue, ...ids] : ids;
}

export function nextTypeaheadIndex<T>(options: readonly DropdownOption<T>[], key: string, fromIndex: number): number {
  const letter = key.toLowerCase();
  const count = options.length;
  for (let step = 1; step <= count; step++) {
    const index = (fromIndex + step + count) % count;
    const option = options[index];
    if (!option.disabled && option.label.toLowerCase().startsWith(letter)) return index;
  }
  return -1;
}

/**
 * Status indicator colors for dropdown options
 */
export type DropdownOptionStatus = 'success' | 'warning' | 'danger' | 'info';

/**
 * Option interface for dropdown items
 */
export interface DropdownOption<T = string> {
  /** Display label for the option */
  label: string;
  /** Value of the option */
  value: T;
  /** Whether the option is disabled */
  disabled?: boolean;
  /** Optional group label — options with the same group are rendered under a shared header */
  group?: string;
  /** Optional icon name for zagIcon directive (e.g., 'home', 'person', 'settings') */
  icon?: string;
  /** Optional custom swatch color (hex/rgb/css color) */
  color?: string;
  /** Optional image/avatar URL for entity-backed options */
  imageUrl?: string | null;
  /** Optional avatar display name. When present, render a real avatar. */
  avatarName?: string;
  /** Optional explicit avatar source. Falls back to imageUrl when omitted. */
  avatarSrc?: string | null;
  /** Optional status indicator (renders a colored dot) */
  status?: DropdownOptionStatus;
  /** Optional badge text displayed right-aligned at the end of the option row */
  badge?: string;
}

/**
 * Size variants for dropdown
 */
export type DropdownSize = 'sm' | 'md' | 'lg';

/**
 * Marker for content projected into the search row, right-aligned after the search input.
 * Use for icon controls (e.g. sort buttons) that must receive clicks while the panel is open.
 */
@Directive({
  selector: '[zagDropdownSearchAction]',
})
export class DropdownSearchAction {}

/**
 * Dropdown component for selecting a single value from a list of options.
 *
 * @example
 * ```html
 * <zag-dropdown
 *   [options]="options"
 *   [placeholder]="'Select an option'"
 *   [(ngModel)]="selectedValue"
 * ></zag-dropdown>
 * ```
 *
 * @example
 * ```html
 * <zag-dropdown
 *   [options]="options"
 *   formControlName="category"
 * ></zag-dropdown>
 * ```
 *
 * @example Trailing passive content only (e.g. tooltip icon, never buttons)
 * ```html
 * <zag-dropdown [options]="opts">
 *   <span zagDropdownTrailing zagIcon="info" [zagTooltip]="'…'"></span>
 * </zag-dropdown>
 * ```
 * Clicking the projected element does NOT toggle the panel.
 *
 * @example Interactive search-row controls (e.g. sort buttons)
 * ```html
 * <zag-dropdown [options]="opts" [searchable]="true">
 *   <div zagDropdownSearchAction>
 *     <button type="button" (click)="sortByName()">Name</button>
 *   </div>
 * </zag-dropdown>
 * ```
 * Search-action content renders inside the overlay panel, so clicks reach it
 * instead of the backdrop.
 */
@Component({
  selector: 'zag-dropdown',
  templateUrl: './dropdown.html',
  styleUrl: './dropdown.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayModule, TranslatePipe, ScrollIntoViewDirective, EntityVisualComponent, Avatar, ZagIcon],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Dropdown),
      multi: true,
    },
  ],
  host: {
    class: 'zag-dropdown',
    '[class.zag-dropdown--open]': 'isOpen()',
    '[class.zag-dropdown--disabled]': 'disabled()',
    '[class.zag-dropdown--readonly]': 'readonly()',
    '[class.zag-dropdown--focused]': 'focused()',
    '[class.zag-dropdown--sm]': 'size() === "sm"',
    '[class.zag-dropdown--md]': 'size() === "md"',
    '[class.zag-dropdown--lg]': 'size() === "lg"',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class Dropdown<T = string> implements ControlValueAccessor, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly document = inject(DOCUMENT);
  private readonly formField = inject(FormField, { optional: true });

  private readonly mobileQuery =
    typeof this.document.defaultView?.matchMedia === 'function' ? this.document.defaultView.matchMedia('(max-width: 539px)') : null;
  protected readonly isMobile = signal<boolean>(this.mobileQuery?.matches ?? false);
  private readonly onMobileChange = (e: MediaQueryListEvent) => {
    this.isMobile.set(e.matches);
    this.cdr.markForCheck();
  };

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  private readonly destroyRef = inject(DestroyRef);
  /** Emits raw search input; debounced before re-emitting via `searchChange`. */
  private readonly searchInput$ = new Subject<string>();

  constructor() {
    this.mobileQuery?.addEventListener('change', this.onMobileChange);

    // Debounce search input so server-side consumers do not fire a request on
    // every keystroke. The term is trimmed and de-duplicated before emission.
    this.searchInput$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(term => this.searchChange.emit(term.trim()));

    // In server-filtered mode the option list updates asynchronously after a
    // search. Keep the highlight on the first option so keyboard commit works.
    effect(() => {
      if (this.serverFiltered() && this.isOpen()) {
        const count = this.options().length;
        this.highlightedIndex.set(count > 0 ? 0 : -1);
      }
    });
  }

  /** Available options for the dropdown */
  readonly options = input.required<DropdownOption<T>[]>();

  /** Placeholder text when no option is selected */
  readonly placeholder = input<string>('Select an option');

  /** Whether the dropdown is disabled */
  readonly disabled = input<boolean>(false);

  /** Whether the dropdown is read-only: shows the selected value as plain text and cannot be opened */
  readonly readonly = input<boolean>(false);

  private readonly inert = computed(() => this.disabled() || this.readonly());

  /** Optional id attribute */
  readonly id = input<string | undefined>(undefined);

  /** Optional name attribute */
  readonly name = input<string | undefined>(undefined);

  /** Whether the dropdown is required */
  readonly required = input<boolean>(false);

  /** Size variant */
  readonly size = input<DropdownSize>('md');

  readonly panelWidth = input<'trigger' | 'content'>('trigger');

  /** Whether to show a clear button when a value is selected */
  readonly clearable = input<boolean>(false);

  /** Multi-select mode; selection delivered via ngModel/CVA as T[] and via multiSelectionChange; selectionChange stays single-mode only. */
  readonly multiple = input<boolean>(false);

  /** Word after the count when 2+ selected. */
  readonly selectedCountText = input<string>('selected');

  /**
   * Marker value of a synthetic select-all option; only meaningful with `multiple=true`.
   * Consumer owns toggle semantics — when this value is present in the selection
   * the trigger shows that option's label instead of the count.
   */
  readonly selectAllValue = input<T | undefined>(undefined);

  /**
   * When true, renders a search field at the top of the panel that filters the
   * options by label (case-insensitive). Use for long option lists such as
   * person pickers.
   */
  readonly searchable = input<boolean>(false);

  /**
   * When true, the component does NOT filter options locally — it only emits
   * {@link searchChange} so the consumer can perform a server-side search and
   * feed back the resulting `options`. The list is rendered exactly as provided.
   * Has no effect unless {@link searchable} is also true.
   */
  readonly serverFiltered = input<boolean>(false);

  /** Placeholder text for the in-panel search field. */
  readonly searchPlaceholder = input<string>('Search…');

  /** Message shown in the panel when the search term matches no options. */
  readonly noResultsText = input<string>('No results');

  /** Accessible label for the in-panel search field. */
  readonly searchAriaLabel = input<string>('Filter options');

  /** Accessible label for the listbox panel (falls back to placeholder when not set) */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Accessible label for the clear-selection button */
  readonly clearAriaLabel = input<string>('Clear selection');

  /** Current value (for direct binding without forms) */
  @Input()
  set value(val: T | T[] | null) {
    if (Array.isArray(val)) {
      this.multiValues.set([...val]);
    } else {
      this.selectedValue.set(val);
    }
  }

  /** Emitted when the selection changes */
  readonly selectionChange = output<T | null>();

  /** Emitted with the full selection array when the multi-select selection changes */
  readonly multiSelectionChange = output<T[]>();

  /**
   * Emitted (debounced) when the in-panel search term changes. Use together
   * with {@link serverFiltered} to drive a server-side search. Fires with the
   * trimmed term, and with an empty string when the field is cleared.
   */
  readonly searchChange = output<string>();

  /** Internal state */
  protected readonly isOpen = signal<boolean>(false);
  protected readonly focused = signal<boolean>(false);
  protected readonly highlightedIndex = signal<number>(-1);
  private readonly selectedValue = signal<T | null>(null);
  private readonly multiValues = signal<T[]>([]);
  /** Current search term (only meaningful when `searchable` is true). */
  protected readonly searchTerm = signal<string>('');

  /** Overlay positions for cdkConnectedOverlay */
  protected readonly overlayPositions: ConnectedPosition[] = [
    // Prefer bottom
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    // Fallback to top
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  /** On mobile the overlay is centred in the viewport via the panel class. */
  protected readonly mobileOverlayPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'center' },
  ];

  /** Staged single value on mobile — committed by Apply, discarded on close. */
  private readonly pendingSingle = signal<T | null>(null);
  /** Staged multi values on mobile — committed by Apply, discarded on close. */
  private readonly pendingMulti = signal<T[]>([]);

  /** Label auto-detected from parent FormField for mobile overlay header */
  protected readonly fieldLabel = computed(() => this.formField?.label() ?? '');

  /** Title shown in the mobile dialog header. */
  protected readonly mobileTitle = computed(() => this.fieldLabel() || this.ariaLabel() || this.placeholder());

  /** Pending selection resolved against options. Null only when nothing staged matches. */
  protected readonly pendingSelectedOption = computed(() => {
    const pending = this.pendingSingle();
    return this.options().find(opt => opt.value === pending) ?? null;
  });

  /** Apply is enabled on mobile once the staged selection resolves to an option. Multi can always commit, even empty. */
  protected readonly canApplyMobile = computed(() => (this.multiple() ? true : this.pendingSelectedOption() !== null));

  /** Computed: Currently selected option */
  readonly selectedOption = computed(() => {
    const value = this.selectedValue();
    // Don't early-return on null - null can be a valid option value
    return this.options().find(opt => opt.value === value) ?? null;
  });

  /**
   * Computed: Options visible in the panel. When `searchable` is on and a search
   * term is present, the list is filtered by a case-insensitive label match.
   * When `serverFiltered` is on, no local filtering happens — the consumer is
   * responsible for supplying the already-filtered `options`. All keyboard
   * navigation, option ids, and highlighting operate on THIS list.
   */
  readonly displayedOptions = computed(() => {
    if (!this.searchable() || this.serverFiltered()) return this.options();
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return this.options();
    return this.options().filter(opt => fuzzyMatch(term, opt.label) || (opt.group ? fuzzyMatch(term, opt.group) : false));
  });

  /**
   * Returns the group label for a given index if it starts a new group,
   * or null if the option belongs to the same group as the previous one.
   * Used by the template to render group headers.
   */
  protected groupHeaderAt(index: number): string | null {
    const opts = this.displayedOptions();
    const group = opts[index]?.group;
    if (!group) return null;
    if (index === 0) return group;
    return opts[index - 1]?.group !== group ? group : null;
  }

  /** Computed: Display text for the trigger */
  readonly displayText = computed(() => {
    if (this.multiple()) {
      const marker = this.selectAllValue();
      if (marker !== undefined && this.multiValues().includes(marker)) {
        return this.options().find(opt => opt.value === marker)?.label ?? this.placeholder();
      }
      const values = this.multiValues();
      if (values.length === 0) return this.placeholder();
      if (values.length === 1) return this.options().find(opt => opt.value === values[0])?.label ?? this.placeholder();
      return `${values.length} ${this.selectedCountText()}`;
    }
    const selected = this.selectedOption();
    return selected ? selected.label : this.placeholder();
  });

  /** Computed: Whether placeholder is shown */
  readonly showPlaceholder = computed(() => {
    if (this.multiple()) return this.multiValues().length === 0;
    // Show placeholder only when there's no matching option (not just null value)
    return this.selectedOption() === null;
  });

  /** Computed: Whether to show the clear button */
  readonly showClearButton = computed(() => {
    if (this.multiple()) return this.clearable() && this.multiValues().length > 0 && !this.inert();
    return this.clearable() && this.selectedOption() !== null && !this.inert();
  });

  /** Computed: Enabled options for keyboard navigation */
  readonly enabledOptions = computed(() => {
    return this.displayedOptions().filter(opt => !opt.disabled);
  });

  /** Computed: Whether the search field currently matches no options. */
  readonly showNoResults = computed(() => this.searchable() && this.searchTerm().trim().length > 0 && this.displayedOptions().length === 0);

  // ControlValueAccessor callbacks
  private onChange: (value: T | T[] | null) => void = () => {
    // Default no-op
  };
  private onTouched: () => void = () => {
    // Default no-op
  };

  ngOnDestroy(): void {
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
  }

  writeValue(value: T | T[] | null): void {
    if (this.multiple()) {
      this.multiValues.set(Array.isArray(value) ? [...value] : []);
    } else {
      this.selectedValue.set(Array.isArray(value) ? null : value);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: T | T[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    this.cdr.markForCheck();
  }

  /**
   * Toggle dropdown open state
   */
  toggle(): void {
    if (this.inert()) return;

    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the dropdown
   */
  open(): void {
    if (this.inert() || this.isOpen()) return;

    this.searchTerm.set('');
    this.pendingSingle.set(this.selectedValue());
    this.pendingMulti.set([...this.multiValues()]);
    this.isOpen.set(true);
    this.focused.set(true);

    // Set highlighted index to selected option or first enabled option, relative
    // to the displayed (post-filter) list.
    const selectedIdx = this.multiple()
      ? this.displayedOptions().findIndex(opt => this.multiValues().includes(opt.value))
      : this.displayedOptions().findIndex(opt => opt.value === this.selectedValue());
    this.highlightedIndex.set(Math.max(selectedIdx, 0));

    // Move focus into the search field so the user can type immediately.
    if (this.searchable()) {
      requestAnimationFrame(() => this.searchInputRef?.nativeElement.focus());
    }
  }

  /**
   * Close the dropdown
   */
  close(): void {
    this.isOpen.set(false);
    this.highlightedIndex.set(-1);
    this.searchTerm.set('');
    this.pendingSingle.set(null);
    this.pendingMulti.set([]);
  }

  /** Commit the staged selection (mobile only). */
  protected applyMobile(): void {
    if (this.multiple()) {
      const next = [...this.pendingMulti()];
      this.multiValues.set(next);
      this.onChange([...next]);
      this.multiSelectionChange.emit([...next]);
    } else {
      const pending = this.pendingSelectedOption();
      if (pending) {
        this.selectedValue.set(pending.value);
        this.onChange(pending.value);
        this.selectionChange.emit(pending.value);
      }
    }
    this.close();
  }

  /** Close without applying (mobile cancel). */
  protected closeMobile(): void {
    this.close();
  }

  /**
   * Update the search term and reset the highlight to the first matching option.
   * In `serverFiltered` mode the (debounced) term is emitted via `searchChange`
   * so the consumer can fetch matching options.
   */
  onSearchInput(value: string): void {
    this.searchTerm.set(value);
    if (this.serverFiltered()) {
      this.searchInput$.next(value);
      return;
    }
    this.highlightedIndex.set(this.displayedOptions().length > 0 ? 0 : -1);
  }

  /**
   * Select an option
   */
  selectOption(option: DropdownOption<T>): void {
    if (option.disabled) return;

    if (this.isMobile() && this.isOpen()) {
      const idx = this.displayedOptions().findIndex(opt => opt.value === option.value);
      if (this.multiple()) {
        const current = this.pendingMulti();
        this.pendingMulti.set(current.includes(option.value) ? current.filter(v => v !== option.value) : [...current, option.value]);
      } else {
        this.pendingSingle.set(option.value);
      }
      if (idx >= 0) this.highlightedIndex.set(idx);
      return;
    }

    if (this.multiple()) {
      const current = this.multiValues();
      const next = current.includes(option.value) ? current.filter(v => v !== option.value) : [...current, option.value];
      this.multiValues.set(next);
      this.onChange([...next]);
      this.multiSelectionChange.emit([...next]);
      return;
    }

    this.selectedValue.set(option.value);
    this.onChange(option.value);
    this.selectionChange.emit(option.value);
    this.close();
  }

  /**
   * Clear the current selection
   */
  clearSelection(event: Event): void {
    event.stopPropagation(); // Prevent toggle from firing
    if (this.multiple()) {
      this.multiValues.set([]);
      this.onChange([]);
      this.multiSelectionChange.emit([]);
      return;
    }
    this.selectedValue.set(null);
    this.onChange(null);
    this.selectionChange.emit(null);
  }

  /**
   * Check if an option is selected
   */
  isSelected(option: DropdownOption<T>): boolean {
    if (this.multiple()) {
      const staged = this.isMobile() && this.isOpen() ? this.pendingMulti() : this.multiValues();
      return staged.includes(option.value);
    }
    if (this.isMobile() && this.isOpen()) return this.pendingSingle() === option.value;
    return this.selectedValue() === option.value;
  }

  /**
   * Check if an option is highlighted
   */
  isHighlighted(index: number): boolean {
    return this.highlightedIndex() === index;
  }

  hasEntityVisual(option: DropdownOption<T> | null | undefined): boolean {
    return !!(option?.imageUrl || option?.icon);
  }

  hasAvatarVisual(option: DropdownOption<T> | null | undefined): boolean {
    return !!option?.avatarName;
  }

  resolveAvatarSrc(option: DropdownOption<T> | null | undefined): string | null | undefined {
    return option?.avatarSrc ?? option?.imageUrl;
  }

  /**
   * Handle focus on the trigger
   */
  onTriggerFocus(): void {
    this.focused.set(true);
  }

  /**
   * Handle blur on the trigger
   */
  onTriggerBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  /**
   * Handle keyboard navigation
   */
  onKeyDown(event: KeyboardEvent): void {
    if (this.inert()) return;

    // When the in-panel search field is focused, Space and printable characters
    // must reach the input. Only intercept the navigation/commit keys.
    const searchingInInput = this.searchable() && this.isOpen() && event.target === this.searchInputRef?.nativeElement;

    const handlers: Record<string, () => void> = {
      Enter: () => this.handleEnterOrSpace(event),
      Escape: () => this.handleEscape(event),
      ArrowDown: () => this.handleArrowDown(event),
      ArrowUp: () => this.handleArrowUp(event),
      Home: () => this.handleHome(event),
      End: () => this.handleEnd(event),
      Tab: () => this.handleTab(),
    };
    if (!searchingInInput) {
      handlers[' '] = () => this.handleEnterOrSpace(event);
    }

    const handler = handlers[event.key];
    if (handler) handler();
    else if (!searchingInInput) this.handleTypeahead(event);
  }

  private handleTypeahead(event: KeyboardEvent): void {
    if (!this.isOpen() || event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    const index = nextTypeaheadIndex(this.displayedOptions(), event.key, this.highlightedIndex());
    if (index >= 0) this.highlightedIndex.set(index);
  }

  private handleEnterOrSpace(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.isOpen()) {
      this.open();
      return;
    }

    const highlighted = this.highlightedIndex();
    if (highlighted < 0) return;

    const option = this.displayedOptions()[highlighted];
    if (option && !option.disabled) {
      this.selectOption(option);
    }
  }

  private handleEscape(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.isMobile()) this.closeMobile();
    else this.close();
  }

  private handleArrowDown(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.isOpen()) {
      this.highlightNext();
    } else {
      this.open();
    }
  }

  private handleArrowUp(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.isOpen()) {
      this.highlightPrevious();
    } else {
      this.open();
    }
  }

  private handleHome(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.isOpen()) {
      this.highlightFirst();
    }
  }

  private handleEnd(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.isOpen()) {
      this.highlightLast();
    }
  }

  private handleTab(): void {
    if (this.isOpen()) {
      this.close();
    }
  }

  /**
   * Handle mouse enter on option for highlighting
   */
  onOptionMouseEnter(index: number): void {
    const option = this.displayedOptions()[index];
    if (!option?.disabled) {
      this.highlightedIndex.set(index);
    }
  }

  private highlightNext(): void {
    const options = this.displayedOptions();
    if (options.length === 0) return;
    let index = this.highlightedIndex();
    const startIndex = index;

    do {
      index = (index + 1) % options.length;
      if (!options[index].disabled) {
        this.highlightedIndex.set(index);
        return;
      }
    } while (index !== startIndex);
  }

  private highlightPrevious(): void {
    const options = this.displayedOptions();
    if (options.length === 0) return;
    let index = this.highlightedIndex();
    const startIndex = index;

    do {
      index = index <= 0 ? options.length - 1 : index - 1;
      if (!options[index].disabled) {
        this.highlightedIndex.set(index);
        return;
      }
    } while (index !== startIndex);
  }

  private highlightFirst(): void {
    const options = this.displayedOptions();
    for (let i = 0; i < options.length; i++) {
      if (!options[i].disabled) {
        this.highlightedIndex.set(i);
        return;
      }
    }
  }

  private highlightLast(): void {
    const options = this.displayedOptions();
    for (let i = options.length - 1; i >= 0; i--) {
      if (!options[i].disabled) {
        this.highlightedIndex.set(i);
        return;
      }
    }
  }
}
